import { useRouter } from "@/app/hooks/useRouter";
import { RouteKeys } from "@/api_framework/api_modals/Routers";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { IconType } from "react-icons";
import { BiTransfer, BiTestTube } from "react-icons/bi";
import { FaRegHospital } from "react-icons/fa6";
import { IoCartOutline, IoPeopleOutline } from "react-icons/io5";
import { MdOutlineInventory2 } from "react-icons/md";
import { RiCoupon2Line } from "react-icons/ri";
import { Link, useNavigate } from "react-router-dom";
import ConfigurationHeaderComponent from "../ConfigurationHeader";
import RBACFlag from "@/app/components/core/RBACFlag";
import { CiMap } from "react-icons/ci";
import { IoGridOutline } from "react-icons/io5";

type LinkItem = {
  label: string;
  actionId?: RouteKeys;
} & (
  | { type: "route"; routeKey: RouteKeys }
  | { type: "click"; path: string }
  | { type: "link"; routeKey: RouteKeys }
  | { type: "external"; url: string }
);

type SectionConfig = {
  actionId: RouteKeys;
  icon: IconType;
  title: string;
  links: LinkItem[];
};

const SECTIONS: SectionConfig[] = [
  {
    actionId: "CLINIC_AND_CITY_SETUP",
    icon: FaRegHospital,
    title: "City & Clinic Setup",
    links: [
      { label: "City Setup", type: "route", routeKey: "CITY_SETUP" },
      { label: "Clinic Setup", type: "route", routeKey: "CLINIC_SETUP" },
      { label: "Equipment Setup", type: "route", routeKey: "EQUIPMENT_SETUP" },
      { label: "Warehouse Setup", type: "route", routeKey: "WAREHOUSE_LIST" },
      {
        label: "Quick Commerce Setup",
        type: "route",
        routeKey: "STORE_TIMING_SETUP",
        actionId: "STORE_TIMING_SETUP",
      },
    ],
  },
  {
    actionId: "STAFF_SETUP_PAGE",
    icon: IoPeopleOutline,
    title: "Staff Setup",
    links: [
      { label: "Staff Setup", type: "route", routeKey: "STAFF_SETUP" },
      { label: "Groomer Setup", type: "route", routeKey: "GROOMER_SETUP" },
      { label: "Groomer Clinic Mapping", type: "route", routeKey: "GROOMER_CLINIC_MAP" },
      { label: "Paravet Setup", type: "route", routeKey: "PARAVET_SETUP" },
      { label: "Paravet Clinic Mapping", type: "route", routeKey: "PARAVET_CLINIC_MAP" },
      { label: "Staff Onboarding", type: "route", routeKey: "STAFF_ONBOARDING" },
      { label: "Staff Clinic Mapping", type: "route", routeKey: "STAFF_CLINIC_MAP" },
      { label: "Staff Category Setup", type: "route", routeKey: "STAFF_CATEGORY_SETUP" },
      { label: "Roles & Permissions", type: "route", routeKey: "ROLES_PERMISSION", actionId: "ROLES_PERMISSION" },
    ],
  },
  {
    actionId: "ROTA_SETUP_PAGE",
    icon: IoPeopleOutline,
    title: "Rota",
    links: [
      { label: "Standard Shifts", type: "click", path: "rota/shift-details" },
      { label: "Staff Rota Setup", type: "click", path: "rota/rota-view" },
      { label: "Booking Fee Configurations", type: "click", path: "rota/booking-fee" },
      { label: "Remove Slots", type: "click", path: "rota/remove-slots", actionId: "REMOVE_SLOTS" },
    ],
  },
  {
    actionId: "QUICK_COMM_PAGE",
    icon: IoCartOutline,
    title: "App Configuration",
    links: [
      { label: "Add Pincode", type: "route", routeKey: "PINCODE_SETUP" },
      { label: "Pincode Weather (Rain) Setup", type: "route", routeKey: "PINCODE_WEATHER_SETUP" },
      { label: "Home Tile Configuration", type: "route", routeKey: "HOME_TILE_SETUP", actionId: "HOME_TILE_SETUP" },
      { label: "Clinic Tag Configuration", type: "route", routeKey: "CLINIC_TAG_SETUP", actionId: "CLINIC_TAG_SETUP" },
    ],
  },
  {
    actionId: "INVENTORY_MODULE_PAGE",
    icon: MdOutlineInventory2,
    title: "Inventory Setup",
    links: [
      { label: "HSN & Tax Setup", type: "click", path: "inventory/hsn-tax" },
      { label: "Batching Setup", type: "click", path: "inventory/batch" },
      { label: "Product Setup", type: "link", routeKey: "PRODUCT_LIST" },
      { label: "Service Setup", type: "click", path: "inventory/service-list" },
      { label: "Manufacturer Setup", type: "click", path: "inventory/manufacture" },
      { label: "Vendor Setup", type: "link", routeKey: "VENDORS_LIST" },
      { label: "BOM Setup", type: "click", path: "inventory/bom-setup" },
      { label: "Item Group Setup", type: "click", path: "inventory/item-group-setup" },
      { label: "Surge Pricing", type: "click", path: "inventory/surge-pricing" },
      { label: "VMS Category Setup", type: "click", path: "inventory/category-setup" },
      { label: "VMS Sub-Category Setup", type: "click", path: "inventory/sub-category-setup" },
      // { label: "VMS Category x Sub-Category Mapping", type: "click", path: "inventory/category-mapping" },
    ],
  },
  {
    actionId: "COUPON_MEMBERSHIP_PAGE",
    icon: RiCoupon2Line,
    title: "Coupons & Membership",
    links: [
      { label: "Coupon Setup", type: "click", path: "coupon-service/coupon-list" },
      { label: "Membership Setup", type: "click", path: "membership-setup/membership-list" },
      {
        label: "Membership Extension Setup",
        type: "click",
        path: "membership-extension/membership-extension-list",
      },
      { label: "Voucher Setup", type: "click", path: "voucher-setup/voucher-list" },
      { label: "Gift Setup", type: "click", path: "gift-setup/gift-list" },
    ],
  },
  {
    actionId: "WHITE_LIST_MOBILE_PAGE",
    icon: BiTransfer,
    title: "Whitelist Mobile",
    links: [
      { label: "WhiteList Mobile Number", type: "click", path: "whitelist-mobile/onboard" },
      { label: "Image To Url Generation", type: "click", path: "whitelist-mobile/image-generation" },
      { label: "Slug Management System", type: "click", path: "whitelist-mobile/slug-management" },
      {
        label: "VMS Documentation",
        type: "external",
        url: "https://notebooklm.google.com/notebook/aa78391c-2236-4ac2-8cd3-280beef95c09",
      },
    ],
  },
  {
    actionId: "LAB_PARTNER_ONBOARDING",
    icon: BiTestTube,
    title: "Lab Configuration",
    links: [
      { label: "Lab Partner Onboarding", type: "click", path: "clinic-lab-report/lab-partner-list" },
      { label: "Lab Partner Mapping", type: "click", path: "clinic-lab-report/lab-partner-clinic-list" },
    ],
  },
  {
    actionId: "APPOINTMENT_CART_ITEM_MAPPING",
    icon: CiMap,
    title: "Mappings",
    links: [{ label: "Appointment Cart Item Mapping", type: "click", path: "mappings/appointment-cart-item-mapping" }],
  },
  {
    actionId: "TILES_CONFIGURATION",
    icon: IoGridOutline,
    title: "Tiles Configuration",
    links: [{ label: "Tiles Configuration", type: "click", path: "tiles/tiles-configuration" }],
  },
];

const linkClassName ="block px-4 py-2 text-sm cursor-pointer hover:bg-gray-50";

const ConfigurationDisclosureComponent = () => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleClick = (path: string) => navigate(`/app/${path}`);

  const renderLink = (link: LinkItem) => {
    let content: React.ReactNode;
    switch (link.type) {
      case "route":
        content = (
          <p className={linkClassName} onClick={() => navigate(getRouteKey(link.routeKey, "path"))}>
            {link.label}
          </p>
        );
        break;
      case "click":
        content = (
          <p className={linkClassName} onClick={() => handleClick(link.path)}>
            {link.label}
          </p>
        );
        break;
      case "link":
        content = (
          <Link to={getRouteKey(link.routeKey, "url")} className={linkClassName}>
            {link.label}
          </Link>
        );
        break;
      case "external":
        content = (
          <p className={linkClassName} onClick={() => window.open(link.url, "_blank")}>
            {link.label}
          </p>
        );
        break;
    }
    return link.actionId ? (
      <RBACFlag key={link.label} actionId={link.actionId}>
        {content}
      </RBACFlag>
    ) : (
      <span key={link.label}>{content}</span>
    );
  };

  const renderSection = (section: SectionConfig, index: number) => {
    const Icon = section.icon;
    const open = openIndex === index;
    return (
      <RBACFlag key={section.actionId} actionId={section.actionId}>
        <div
          className={`w-full max-w-md ${index === 0 ? "" : "mt-4"} border border-slate-400 border-opacity-40 rounded-lg overflow-hidden`}
        >
          <button
            type="button"
            className={`flex w-full p-3 items-center justify-between text-left text-sm
                ${open ? "bg-[#F5F5F5]" : "bg-white"}
              `}
            onClick={() => setOpenIndex(open ? null : index)}
          >
            <div className="flex gap-3 items-center">
              <Icon />
              <span>{section.title}</span>
            </div>
            <ChevronDownIcon className={`${open ? "rotate-180 transform" : ""} h-5 w-5 text-black`} />
          </button>
          {open && <div>{section.links.map(renderLink)}</div>}
        </div>
      </RBACFlag>
    );
  };

  return (
    <>
      <ConfigurationHeaderComponent />
      <div className="w-full py-2 px-4 bg-white rounded-md ">
        <div className="rounded-2xl bg-white p-2">{SECTIONS.map(renderSection)}</div>
      </div>
    </>
  );
};

export default ConfigurationDisclosureComponent;
